import { useMemo, useState } from 'react';
import { Filter, Pencil, Plus, Search, Ticket, Trash2 } from 'lucide-react';
import {
  BosDurum,
  Buton,
  Girdi,
  OnayDialog,
  Rozet,
  VeriTablo,
  toast,
  type ColumnDef,
  type RozetTonu,
} from '@emanetx/ui';
import type { Kupon, KuponTipi } from '@emanetx/sdk';
import { useKuponSil, useKuponlar } from './api.js';
import { KuponDialog } from './kupon-dialog.js';

const TIP_ETIKET: Record<KuponTipi, string> = {
  COK_KULLANIMLIK: 'Çoklu',
  TEKIL: 'Tekil',
  ILK_KULLANICI: 'İlk Kul.',
  PARTNER_OZEL: 'Partner',
};

const TIP_TONU: Record<KuponTipi, RozetTonu> = {
  COK_KULLANIMLIK: 'mavi',
  TEKIL: 'turuncu',
  ILK_KULLANICI: 'yesil',
  PARTNER_OZEL: 'fume',
};

export function SayfaKuponlar() {
  const [sayfa, setSayfa] = useState(1);
  const [arama, setArama] = useState('');
  const [aktif, setAktif] = useState<'' | 'evet' | 'hayir'>('');
  const [dialogAcik, setDialogAcik] = useState(false);
  const [duzenleHedef, setDuzenleHedef] = useState<Kupon | null>(null);
  const [silHedef, setSilHedef] = useState<Kupon | null>(null);

  const sorgu = {
    sayfa,
    boyut: 50,
    ...(arama && { arama }),
    ...(aktif === 'evet' && { aktif: true }),
    ...(aktif === 'hayir' && { aktif: false }),
  };

  const liste = useKuponlar(sorgu);
  const sil = useKuponSil();

  const kolonlar = useMemo<ColumnDef<Kupon, unknown>[]>(
    () => [
      {
        accessorKey: 'kod',
        header: 'Kod',
        meta: { etiket: 'Kod' },
        cell: ({ row }) => (
          <span className="text-marka-700 dark:text-marka-400 font-mono font-semibold">
            {row.original.kod}
          </span>
        ),
      },
      {
        accessorKey: 'tip',
        header: 'Tip',
        meta: { etiket: 'Tip' },
        cell: ({ row }) => (
          <Rozet ton={TIP_TONU[row.original.tip]}>{TIP_ETIKET[row.original.tip]}</Rozet>
        ),
      },
      {
        accessorKey: 'indirimDegeri',
        header: 'İndirim',
        meta: { etiket: 'İndirim' },
        cell: ({ row }) => (
          <span className="font-mono text-sm font-semibold">
            {row.original.indirimTipi === 'YUZDE'
              ? `%${Number(row.original.indirimDegeri).toFixed(0)}`
              : `${row.original.indirimDegeri} ${row.original.paraBirimi ?? ''}`}
            {row.original.maxIndirimTutar && (
              <span className="text-fume-400 ml-1 text-xs font-normal">
                (max {row.original.maxIndirimTutar})
              </span>
            )}
          </span>
        ),
      },
      {
        id: 'gecerlilik',
        header: 'Geçerlilik',
        meta: { etiket: 'Geçerlilik' },
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-fume-500 font-mono text-xs">
            {new Date(row.original.gecerliBaslangic).toLocaleDateString('tr-TR')} →{' '}
            {new Date(row.original.gecerliBitis).toLocaleDateString('tr-TR')}
          </span>
        ),
      },
      {
        id: 'kullanim',
        header: 'Kullanım',
        meta: { etiket: 'Kullanım' },
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {row.original.kullanimSayisi}
            {row.original.toplamLimit ? (
              <span className="text-fume-400"> / {row.original.toplamLimit}</span>
            ) : (
              ''
            )}
          </span>
        ),
      },
      {
        accessorKey: 'aktif',
        header: 'Durum',
        meta: { etiket: 'Durum' },
        cell: ({ row }) =>
          row.original.aktif ? (
            <Rozet ton="yesil" nokta>
              Aktif
            </Rozet>
          ) : (
            <Rozet ton="gri" nokta>
              Pasif
            </Rozet>
          ),
      },
      {
        accessorKey: 'aciklama',
        header: 'Açıklama',
        meta: { etiket: 'Açıklama' },
        cell: ({ row }) => (
          <span className="text-fume-700 dark:text-fume-200 block max-w-xs truncate text-xs">
            {row.original.aciklama}
          </span>
        ),
      },
      {
        id: 'aksiyon',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <Buton
              stil="sade"
              boyut="simge"
              aria-label="Düzenle"
              onClick={() => {
                setDuzenleHedef(row.original);
                setDialogAcik(true);
              }}
            >
              <Pencil size={14} />
            </Buton>
            <Buton
              stil="sade"
              boyut="simge"
              aria-label="Sil"
              onClick={() => setSilHedef(row.original)}
            >
              <Trash2 size={14} className="text-red-500" />
            </Buton>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Kuponlar</h1>
          <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
            İndirim kodları, geçerlilik aralığı ve kullanım limiti yönetimi.
          </p>
        </div>
        <Buton
          onClick={() => {
            setDuzenleHedef(null);
            setDialogAcik(true);
          }}
        >
          <Plus size={16} /> Yeni Kupon
        </Buton>
      </header>

      <VeriTablo
        veri={liste.data?.veriler ?? []}
        kolonlar={kolonlar}
        toplam={liste.data?.toplam}
        sayfa={sayfa}
        boyut={50}
        onSayfa={setSayfa}
        yukleniyor={liste.isLoading}
        yenileniyor={liste.isFetching}
        onYenile={() => void liste.refetch()}
        bosDurum={
          <BosDurum
            ikon={Ticket}
            baslik={arama || aktif ? 'Eşleşen kupon yok' : 'Henüz kupon yok'}
            aciklama={
              arama || aktif
                ? 'Filtreyi temizleyip tekrar deneyin.'
                : 'İlk kuponu ekleyerek başlayın.'
            }
          />
        }
        ustAksiyonlar={
          <>
            <div className="relative min-w-0 flex-1">
              <Search
                size={14}
                className="text-fume-400 pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
              />
              <Girdi
                placeholder="Kod veya açıklama ile ara..."
                value={arama}
                onChange={(e) => {
                  setArama(e.target.value);
                  setSayfa(1);
                }}
                className="h-10 pl-9 text-xs"
              />
            </div>
            <Filter size={13} className="text-fume-400" />
            <select
              value={aktif}
              onChange={(e) => {
                setAktif(e.target.value as '' | 'evet' | 'hayir');
                setSayfa(1);
              }}
              className="border-fume-200 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-10 rounded-lg border bg-white px-3 text-xs"
            >
              <option value="">Tümü</option>
              <option value="evet">Aktif</option>
              <option value="hayir">Pasif</option>
            </select>
          </>
        }
      />

      <KuponDialog
        acik={dialogAcik}
        onDegisim={(o) => {
          setDialogAcik(o);
          if (!o) setDuzenleHedef(null);
        }}
        kupon={duzenleHedef}
      />

      <OnayDialog
        acik={!!silHedef}
        onDegisim={(o) => !o && setSilHedef(null)}
        baslik="Kuponu sil?"
        aciklama={silHedef?.kod}
        onayMetni="Sil"
        tur="tehlike"
        onOnay={async () => {
          if (!silHedef) return;
          try {
            await sil.mutateAsync(silHedef.id);
            toast.success('Kupon silindi');
            setSilHedef(null);
          } catch (err) {
            const m =
              err && typeof err === 'object' && 'message' in err
                ? String((err as { message: string }).message)
                : 'Silinemedi';
            toast.error(m);
          }
        }}
      />
    </div>
  );
}
